import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { TokenUsageMetrics } from 'features/usage/services/usage-service';
import { formatNumber, getAverage } from 'features/usage/utils/usage-format';
import { UsageCardHeader } from './usage-card-header';

function ActivityRow({ label, hint, value }: { label: string; hint: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <span className="shrink-0 text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

export function UsageActivityCard({
  totals,
  dateRange,
  periodDays,
  activeDays,
  promptShare,
  completionShare,
  completionRatio,
}: {
  totals: TokenUsageMetrics;
  dateRange: string;
  periodDays: number;
  activeDays: number;
  promptShare: number;
  completionShare: number;
  completionRatio: string;
}) {
  const { t } = useTranslation();

  return (
    <Card className="min-w-0 rounded-section border-border/70 shadow-none">
      <CardContent className="flex flex-col gap-5 p-5 md:p-6">
        <UsageCardHeader title={t('ACTIVITY', { defaultValue: 'Activity' })} subtitle={dateRange} />

        <div>
          <ActivityRow
            label={t('TURNS')}
            hint={t('TURNS_HINT', { defaultValue: 'Messages sent to Buddy and agents' })}
            value={formatNumber(totals.turn_count)}
          />
          <ActivityRow
            label={t('ACTIVE_DAYS_LABEL', { defaultValue: 'Active days' })}
            hint={t('ACTIVE_DAYS_HINT', { defaultValue: 'Days with at least one turn' })}
            value={t('ACTIVE_DAYS_OF', {
              active: activeDays,
              total: periodDays,
              defaultValue: '{{active}} of {{total}}',
            })}
          />
          <ActivityRow
            label={t('MODEL_CALLS')}
            hint={t('MODEL_CALLS_HINT', { defaultValue: 'One turn can make several calls' })}
            value={formatNumber(totals.model_call_count)}
          />
          <ActivityRow
            label={t('TOKENS_PER_TURN', { defaultValue: 'Tokens per turn' })}
            hint={t('TOKENS_PER_TURN_HINT', { defaultValue: 'Average across the selected range' })}
            value={getAverage(totals.total_tokens, totals.turn_count)}
          />
          <ActivityRow
            label={t('INPUT_VS_OUTPUT', { defaultValue: 'All input vs output' })}
            hint={t('INPUT_VS_OUTPUT_HINT', {
              input: formatNumber(totals.input_tokens),
              output: formatNumber(totals.output_tokens),
              ratio: completionRatio,
              defaultValue: '{{input}} vs {{output}} · ratio {{ratio}}',
            })}
            value={`${promptShare.toFixed(1)}% / ${completionShare.toFixed(1)}%`}
          />
        </div>
      </CardContent>
    </Card>
  );
}
